import { useState, useEffect, useMemo, useRef } from 'react';
import { fetchBeneficiaries } from '../Pages/AddCompanies/Action/action';
import { MapPoint, ClusterPoint, LocationType } from '../Interfaces/Map';
import { Company } from '../Interfaces/Company';
import { ShowAlert } from '../GlobalComponent/Alerts/Alert';
import { fetchCoordinates, isValidCoordinate } from "../GlobalComponent/MapView/mapUtils";

interface Beneficiary {
  _id: string;
  personalInfo: {
    fullName: string;
    mobile: {
      number: string;
    };
    relationshipToSubscriber: string;
  };
  address: Array<{
    label: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    pinCode: string;
    location?: {
      type: string;
      coordinates: [number, number];
    };
  }>;
  subscriber: {
    name: string;
  };
  company: string;
}

const useBeneficiaryMap = ({ companies }: { companies: Company[] }) => {
  const [mapPoints, setMapPoints] = useState<MapPoint[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [clusters, setClusters] = useState<ClusterPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch beneficiary data when component mounts or company changes
  useEffect(() => {
    const loadBeneficiaries = async () => {
      try {
        if (selectedCompany) {
          setLoading(true);
          const response = await fetchBeneficiaries({ companyId: selectedCompany });

          const points: MapPoint[] = generateMapPoints(response || []);

          if (points && Array.isArray(points)) {
            setMapPoints(points);
          } else {
            ShowAlert('error', 'No valid beneficiary data found');
            setMapPoints([]);
          }
        }
      } catch (error) {
        setMapPoints([]);
        ShowAlert('error', 'Error fetching beneficiaries');
      } finally {
        setLoading(false);
      }
    };

    if (selectedCompany) {
      loadBeneficiaries();
    } else {
      setMapPoints([]);
    }
    //eslint-disable-next-line
  }, [selectedCompany, companies]);

  const generateMapPoints = (beneficiaries: Beneficiary[]): MapPoint[] => {
    const points: MapPoint[] = [];
    const seenIds = new Set<string>();

    beneficiaries.forEach(beneficiary => {
      if (!beneficiary.address?.length) {
        return;
      }

      beneficiary.address.forEach((address, index) => {
        const [lat, lng] = fetchCoordinates(address);

        if (!lat || !lng || !isValidCoordinate(lat, lng)) {
          return;
        }

        const pointId = `${beneficiary._id}-${index}`;
        if (!seenIds.has(pointId)) {
          seenIds.add(pointId);

          let type: LocationType = LocationType.OTHER;
          if (address.label?.toLowerCase() === 'home') type = LocationType.HOME;
          else if (address.label?.toLowerCase() === 'work') type = LocationType.WORK;

          const fullAddress = [
            address.addressLine1,
            address.addressLine2,
            address.city,
            address.pinCode
          ].filter(Boolean).join(', ');

          points.push({
            id: pointId,
            lat,
            lng,
            type,
            benId: beneficiary._id,
            fullName: beneficiary.personalInfo?.fullName || 'N/A',
            companyName: beneficiary.company || selectedCompanyName,
            address: fullAddress,
            label: address.label || 'Other'
          });
        }
      });
    });
    return points;
  };

  const filteredCompanies = useMemo(() => {
    return companies.filter(company =>
      company.companyName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companies, searchTerm]);

  const selectedCompanyName = companies.find(c => c._id === selectedCompany)?.companyName || "";

  // Clustering logic
  const createClusters = (points: MapPoint[], distance: number = 0.01): ClusterPoint[] => {
    const clusters: ClusterPoint[] = [];
    const processed = new Set<string>();

    points.forEach(point => {
      if (processed.has(point.id)) return;

      const nearbyPoints = points.filter(p => {
        if (processed.has(p.id) || p.id === point.id) return false;
        const dist = Math.sqrt(
          Math.pow(p.lat - point.lat, 2) + Math.pow(p.lng - point.lng, 2)
        );
        return dist <= distance;
      });

      if (nearbyPoints.length > 0) {
        const clusterPoints = [point, ...nearbyPoints];
        const homeCount = clusterPoints.filter(p => p.type === LocationType.HOME).length;
        const workCount = clusterPoints.filter(p => p.type === LocationType.WORK).length;
        const otherCount = clusterPoints.filter(p => p.type === LocationType.OTHER).length;

        let clusterType: LocationType;
        if (homeCount > 0 && workCount === 0 && otherCount === 0) clusterType = LocationType.HOME;
        else if (workCount > 0 && homeCount === 0 && otherCount === 0) clusterType = LocationType.WORK;
        else if (otherCount > 0 && homeCount === 0 && workCount === 0) clusterType = LocationType.OTHER;
        else clusterType = LocationType.MIXED;

        const avgLat = clusterPoints.reduce((sum, p) => sum + p.lat, 0) / clusterPoints.length;
        const avgLng = clusterPoints.reduce((sum, p) => sum + p.lng, 0) / clusterPoints.length;

        clusters.push({
          id: `cluster-${clusters.length}`,
          lat: avgLat,
          lng: avgLng,
          count: clusterPoints.length,
          points: clusterPoints,
          type: clusterType
        });

        clusterPoints.forEach(p => processed.add(p.id));
      } else {
        clusters.push({
          id: `single-${point.id}`,
          lat: point.lat,
          lng: point.lng,
          count: 1,
          points: [point],
          type: point.type
        });
        processed.add(point.id);
      }
    });

    return clusters;
  };

  // Update clusters when mapPoints change
  useEffect(() => {
    const newClusters = createClusters(mapPoints);
    setClusters(newClusters);
  }, [mapPoints]);

  return {
    // State
    companies,
    selectedCompany,
    setSelectedCompany,
    searchTerm,
    setSearchTerm,
    isOpen,
    setIsOpen,
    inputRef,
    mapPoints,
    clusters,
    loading,

    // Computed values
    filteredCompanies,
    selectedCompanyName,
  };
};

export default useBeneficiaryMap;